/**
 * @fileoverview Opinion schemas for topic-based opinion management
 *
 * Opinions capture Jeremy's positions on specific topics:
 * - serverless-definition: What counts as serverless
 * - cold-starts: Position on cold start concerns
 * - vendor-lock-in: Thoughts on vendor lock-in
 * - etc.
 *
 * Opinions are matched to content via keywords for context-aware generation.
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

/**
 * Strength levels for opinion key points
 */
export const OPINION_STRENGTHS = ['strong', 'moderate', 'mild'];

/**
 * Sentiment levels for opinions - determines tone distribution for blurb generation
 * - strongly-favor: 4 positive, 1 neutral, 1 negative blurbs
 * - favor: 3 positive, 2 neutral, 1 negative blurbs
 * - neutral: 2 positive, 2 neutral, 2 negative blurbs
 * - oppose: 1 positive, 2 neutral, 3 negative blurbs
 * - strongly-oppose: 1 positive, 1 neutral, 4 negative blurbs
 */
export const OPINION_SENTIMENTS = [
  'strongly-favor',
  'favor',
  'neutral',
  'oppose',
  'strongly-oppose',
];

/**
 * Schema for key points within an opinion
 */
export const KeyPointSchema = z
  .object({
    point: z.string().openapi({
      description: 'The key point text',
      example: 'Cold starts are often overblown in importance',
    }),
    strength: z.enum(['strong', 'moderate', 'mild']).openapi({
      description: 'How strongly this point is held',
      example: 'strong',
    }),
    context: z.string().optional().openapi({
      description: 'Additional context for when this point applies',
      example: 'Especially for async/background workloads',
    }),
  })
  .openapi('KeyPoint');

/**
 * Base Opinion schema
 */
const BaseOpinionSchema = z.object({
  id: z.string().openapi({
    description: 'Unique identifier (slug form of topic)',
    example: 'cold-starts',
  }),
  topic: z.string().openapi({
    description: 'Human-readable topic name',
    example: 'Cold Starts',
  }),
  position: z.string().openapi({
    description: 'Summary of the position on this topic',
    example:
      'Cold starts are often overblown. For most workloads, especially async ones, they are rarely the bottleneck. Focus on architecture first.',
  }),
  keyPoints: z.array(KeyPointSchema).openapi({
    description: 'Key points that support this position',
  }),
  keywords: z.array(z.string()).openapi({
    description: 'Keywords for matching this opinion to content',
    example: ['cold start', 'latency', 'provisioned concurrency', 'warmup', 'cold boot'],
  }),
  sentiment: z
    .enum(['strongly-favor', 'favor', 'neutral', 'oppose', 'strongly-oppose'])
    .default('neutral')
    .openapi({
      description:
        'Sentiment level for this opinion - determines tone distribution in generated blurbs',
      example: 'favor',
    }),
  relatedTopics: z.array(z.string()).optional().openapi({
    description: 'IDs of related opinion topics',
    example: ['lambda-best-practices', 'performance-optimization'],
  }),
  examplePhrases: z.array(z.string()).optional().openapi({
    description: 'Example phrases demonstrating this opinion in action',
    example: [
      "I've said this before, but cold starts are rarely the bottleneck",
      "Before you reach for provisioned concurrency, profile your actual workload",
    ],
  }),
  nuances: z.array(z.string()).optional().openapi({
    description: 'Important nuances or exceptions to the position',
    example: [
      'For user-facing sync APIs, cold starts can matter more',
      'VPC-attached functions have additional considerations',
    ],
  }),
  created: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the opinion was created',
    example: '2025-01-15T10:00:00Z',
  }),
  modified: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the opinion was last modified',
    example: '2025-01-15T14:30:00Z',
  }),
});

/**
 * Full Opinion schema
 */
export const OpinionSchema = BaseOpinionSchema.openapi('Opinion');

/**
 * Create schema - omit auto-generated fields
 */
export const CreateOpinionSchema = OpinionSchema.omit({
  created: true,
  modified: true,
}).openapi('CreateOpinion');

/**
 * Update schema - partial, omit immutable fields
 */
export const UpdateOpinionSchema = BaseOpinionSchema.partial()
  .omit({
    id: true,
    created: true,
    modified: true,
  })
  .openapi('UpdateOpinion');

export const OpinionResponseSchema = OpinionSchema.openapi('OpinionResponse');

export const OpinionListResponseSchema = createListResponseSchema(
  OpinionResponseSchema,
  'opinions',
  'OpinionListResponse'
);

/**
 * Schema for opinion matching results
 */
export const OpinionMatchResultSchema = z
  .object({
    opinion: OpinionSchema,
    matchScore: z.number().min(0).max(1).openapi({
      description: 'Relevance score (0-1) based on keyword matches',
      example: 0.85,
    }),
    matchedKeywords: z.array(z.string()).openapi({
      description: 'Keywords that matched the content',
      example: ['cold start', 'latency'],
    }),
  })
  .openapi('OpinionMatchResult');

export const OpinionMatchResponseSchema = z
  .object({
    matches: z.array(OpinionMatchResultSchema).openapi({
      description: 'Opinions matching the content, sorted by relevance',
    }),
    contentKeywords: z.array(z.string()).openapi({
      description: 'Keywords extracted from the content',
    }),
  })
  .openapi('OpinionMatchResponse');

/**
 * @typedef {z.infer<typeof OpinionSchema>} OpinionType
 */
export const OpinionType = /** @type {OpinionType} */ ({});
